/**
 * CleaningGateStrip — "can this batch start on the equipment it is bound to?", on the batch itself.
 *
 * ux-production-supervisor round 2, N1 (BLOCKER): a fail-closed cleaning gate sits on the
 * operation-start path and refuses with HTTP 409 when the equipment is not clean to the grade the
 * batch needs. Sixteen machines fail it at the time of writing. Nothing in the admin app called it,
 * so the supervisor's first warning was an operator ringing him about a red box on a tablet.
 *
 * This strip asks the same question the gate asks, ahead of time, and renders the gate's own
 * sentence — the refusal text here is the identical string the 409 carries, so the screen and the
 * tablet say the same words. It never fails closed itself: if the endpoint is unreachable it says
 * the position is unknown rather than implying the batch is clear to run.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Collapse, Spin, Tag, Typography } from 'antd';
import DataTable from '@/components/DataTable';
import { RefreshCw } from 'lucide-react';
import StatusIndicator, { StatusNotice } from '@/components/genealogy/StatusIndicator';
import { cleaningGate, cleanStateColor, cleanStateLabel } from '@/utils/cleaningApi';

const { Text } = Typography;

const when = (iso) =>
  iso ? new Date(iso).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

// EXR-W1 W1-3: the board renders the server's per-machine VERDICT (CLEAN · NOT_CLEAN · NOT_APPLICABLE ·
// UNKNOWN, source EBMR) with its reason and the plant's mode — never the `ok` flag.
const VERDICT_LABEL = { CLEAN: 'Clean', NOT_CLEAN: 'Not clean', NOT_APPLICABLE: 'Not applicable', UNKNOWN: 'Unknown' };
const MODE_LABEL = { enforce: 'enforce', shadow: 'shadow', accept: 'accept' };
const verdictLabel = (v) => VERDICT_LABEL[v] || String(v || '—').replace(/_/g, ' ');
/** A NOT_CLEAN refuses in every mode; an UNKNOWN only under enforce (else it is shown, not blocking). */
const verdictState = (v, mode) =>
  v === 'CLEAN' ? 'ok' : v === 'NOT_APPLICABLE' ? 'neutral' : v === 'NOT_CLEAN' || mode === 'enforce' ? 'error' : 'warning';

const columns = (mode) => [
  {
    title: 'Equipment',
    dataIndex: 'machine',
    key: 'machine',
    render: (v, r) => (
      <span style={{ fontWeight: 600 }}>
        {v || `#${r.machine_id}`}
      </span>
    ),
  },
  {
    title: 'Verdict',
    dataIndex: 'verdict',
    key: 'verdict',
    render: (v) => <StatusIndicator state={verdictState(v, mode)} label={verdictLabel(v)} />,
  },
  {
    title: 'Cleaning state',
    dataIndex: 'state',
    key: 'state',
    render: (v) => (v ? <Tag color={cleanStateColor(v)}>{cleanStateLabel(v)}</Tag> : '—'),
  },
  { title: 'Needs', dataIndex: 'required_class', key: 'required_class', render: (v) => (v ? <Tag>{String(v).replace(/_/g, ' ')}</Tag> : '—') },
  { title: 'Clean valid until', dataIndex: 'valid_until', key: 'valid_until', render: when },
  {
    title: 'Why',
    dataIndex: 'reason',
    key: 'reason',
    render: (v, r) => (v ? <Text type={verdictState(r.verdict, mode) === 'error' ? 'danger' : 'secondary'}>{v}</Text>
      : <Text type="secondary">{r.verdict === 'CLEAN' ? 'clean to the grade this batch needs' : '—'}</Text>),
  },
];

export default function CleaningGateStrip({ batchNumber, operationId = null, compact = false, started, style }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  const load = useCallback(async () => {
    if (!batchNumber) return;
    setState((s) => ({ ...s, loading: true }));
    const res = await cleaningGate(batchNumber, operationId);
    // `gate/` answers 409 *with a body* when it would refuse — that is the answer, not a failure.
    if (res?.data && typeof res.data.ok === 'boolean') {
      setState({ loading: false, data: res.data, error: null });
    } else {
      setState({ loading: false, data: null, error: res?.message || 'Could not read the cleaning gate' });
    }
  }, [batchNumber, operationId]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.loading && !state.data) {
    return (
      <div style={{ padding: compact ? 6 : 10, ...style }}>
        <Spin size="small" /> <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>Checking the cleaning gate…</Text>
      </div>
    );
  }

  if (state.error) {
    return (
      <StatusNotice state="warning" title="Cleaning position unknown" style={{ marginBottom: 12, ...style }}>
        <div style={{ fontSize: 12 }}>
          The cleaning gate could not be read ({state.error}). Do not take this as a pass — the gate still runs
          when the operator presses Start.
        </div>
        <Button size="small" icon={<RefreshCw size={13} />} onClick={load} style={{ marginTop: 6 }}>Retry</Button>
      </StatusNotice>
    );
  }

  const d = state.data;
  if (!d) return null;
  const mode = d.mode;
  const machines = d.machines || [];
  // What the server says, machine by machine: the verdict, never `ok`.
  const notClean = machines.filter((m) => m.verdict === 'NOT_CLEAN');
  const unknown = machines.filter((m) => m.verdict === 'UNKNOWN');
  const issues = [...notClean, ...unknown];
  const rows = (issues.length ? issues : machines).map((m, i) => ({ ...m, key: m.machine_id ?? i }));
  const causes = Object.entries(issues.reduce((acc, m) => { const k = m.state || m.verdict || 'UNKNOWN'; acc[k] = (acc[k] || 0) + 1; return acc; }, {})).sort((x, y) => y[1] - x[1]);
  const modeNote = `Cleaning check mode: ${MODE_LABEL[mode] || mode || '—'}`;
  const refresh = (
    <Button size="small" icon={<RefreshCw size={13} />} onClick={load} loading={state.loading} style={{ marginTop: 8 }}>
      Re-check
    </Button>
  );
  const machineList = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 0', marginTop: 4 }}>
      {machines.map((m) => (
        <StatusIndicator
          key={m.machine_id}
          state={verdictState(m.verdict, mode)}
          label={`${m.machine || `#${m.machine_id}`} · ${verdictLabel(m.verdict)}${m.state ? ` (${cleanStateLabel(m.state)})` : ''}`}
          tooltip={m.reason || undefined}
        />
      ))}
    </div>
  );

  if (d.no_equipment_bound) {
    return (
      <StatusNotice state="info" title="Cleaning check: this operation binds no equipment" style={{ marginBottom: 12, ...style }}>
        <div style={{ fontSize: 12 }}>Nothing to judge — the start is recorded with no equipment bound. {modeNote}.</div>
        {refresh}
      </StatusNotice>
    );
  }

  // Nothing NOT_CLEAN or UNKNOWN.
  if (!issues.length) {
    const title = started === false
      ? `Cleaning check: clear to start — ${machines.length} bound machine${machines.length === 1 ? '' : 's'} judged`
      : `Cleaning check: no bound machine would be refused — ${machines.length} judged`;
    return (
      <StatusNotice state="ok" title={title} style={{ marginBottom: 12, ...style }}>
        {!compact && machineList}
        <div style={{ fontSize: 12, marginTop: 4 }}><Text type="secondary">{modeNote}</Text></div>
        {refresh}
      </StatusNotice>
    );
  }

  // UNKNOWN only, and the plant's mode lets it start (shadow records a finding; accept records the verdict).
  if (!d.refused) {
    return (
      <StatusNotice
        state="warning"
        title={`Cleaning of ${unknown.length} bound machine${unknown.length === 1 ? '' : 's'} cannot be known — ${mode === 'shadow' ? 'the start will be recorded as a finding for QA' : 'the start will record the verdict'}`}
        style={{ marginBottom: 12, ...style }}
      >
        <div style={{ fontSize: 12 }}>{modeNote}. Under enforce these machines would be refused. This is not a pass.</div>
        {!compact && machineList}
        {refresh}
      </StatusNotice>
    );
  }

  return (
    <StatusNotice
      state="error"
      title={started === false
        ? `This batch cannot start on ${issues.length} of its machines`
        : `${issues.length} bound machine${issues.length === 1 ? '' : 's'} would refuse the next step`}
      style={{ marginBottom: 12, ...style }}
    >
      {/* Why they are blocked, counted: one chip per cleaning state, so 49 machines read as three or four causes. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        {causes.map(([st, n]) => (
          <Tag key={st} color={cleanStateColor(st)} style={{ margin: 0, fontSize: 12.5, padding: '2px 10px' }}>
            {cleanStateLabel(st)} · {n}
          </Tag>
        ))}
      </div>
      <Text style={{ display: 'block', fontSize: 12.5, marginBottom: 6 }}>
        To clear them, record an approved clean of the required class against each machine (Logs → Cleaning Logs), then press Re-check.
      </Text>
      <Collapse
        ghost
        size="small"
        items={[{
          key: 'machines',
          label: <Text strong style={{ fontSize: 12.5 }}>{`Show the ${issues.length} machine${issues.length === 1 ? '' : 's'} and why each is blocked`}</Text>,
          children: (
          <DataTable
            tableKey="cleaningGateMachines"
            searchable
            searchPlaceholder="Search equipment…"
            showCount={false}
            bordered
            size="small"
            columns={columns(mode)}
            data={rows}
            height={null}
            minHeight={120}
            scrollX="max-content"
            pagination={{ pageSize: 10 }}
          />
          ),
        }]}
      />
      {/* The gate's own words — byte-for-byte what the operator's 409 will say. Kept in full, folded away because the table above says the same thing per machine. */}
      <Collapse
        ghost
        size="small"
        style={{ marginTop: 6 }}
        items={[{
          key: 'msg',
          label: <Text type="secondary" style={{ fontSize: 12 }}>Read the reason for each machine in words (what the operator sees)</Text>,
          children: Array.isArray(d.refusals) && d.refusals.length ? (
            // the server sends the same sentences as a list, one per machine — show them as one, not as a single paragraph
            <div style={{ fontSize: 12.5 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Cannot start: equipment is not cleaned to the standard this batch requires.</div>
              <ol style={{ margin: 0, paddingInlineStart: 20, display: 'grid', gap: 6 }}>
                {d.refusals.map((r, i) => {
                  const cut = String(r).indexOf(': ');
                  return (
                    <li key={i}>
                      {cut > 0 ? <><b>{String(r).slice(0, cut)}</b>{String(r).slice(cut)}</> : r}
                    </li>
                  );
                })}
              </ol>
            </div>
          ) : <div style={{ fontSize: 12, fontWeight: 500 }}>{d.message}</div>,
        }]}
      />
      <div style={{ marginTop: 6, fontSize: 12 }}>
        <Text type="secondary">{modeNote}. A machine that is not clean refuses in every mode.</Text>
      </div>
      {refresh}
    </StatusNotice>
  );
}

CleaningGateStrip.propTypes = {
  batchNumber: PropTypes.string,
  operationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  compact: PropTypes.bool,
  /** true once the batch has started (server start_datetime); only `false` says "clear to start". */
  started: PropTypes.bool,
  style: PropTypes.object,
};
